/**
 * /dashboard/sso — Org-admin UI for configuring enterprise SSO.
 *
 * Wraps the existing API endpoints in routes/sso.ts:
 *
 *   • PUT  /api/orgs/:orgId/sso/saml   — paste IdP metadata XML
 *   • PUT  /api/orgs/:orgId/sso/oidc   — paste issuer + client id/secret
 *   • DELETE /api/orgs/:orgId/sso      — disable SSO entirely
 *   • GET  /api/orgs/:orgId/sso        — fetch current config
 *   • POST /api/orgs/:orgId/sso/domains/:domain — claim a domain
 *   • DELETE /api/orgs/:orgId/sso/domains/:domain — release a domain
 *   • GET  /api/orgs/:orgId/sso/domains — list claimed domains
 *
 * The page assumes the user has an `activeOrgId` in their JWT (every
 * Rynora user does — personal orgs are created on first login). Org
 * owners and admins can configure SSO; viewers see a read-only banner.
 */

import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Building2, ExternalLink, Loader2, Plus, Trash2 } from 'lucide-react';
import { BrandMark } from '@rynora/theme';
import { getApiBaseUrl } from '../../../lib/config';

const API_BASE: string = getApiBaseUrl();

interface SsoSettingsPageProps {
  onBack: () => void;
}

interface OrgSummary {
  id: string;
  slug: string;
  name: string;
}

interface SamlConfigView {
  idpMetadataXml?: string;
  idpEntityId?: string;
  spEntityId?: string;
  callbackUrl?: string;
}

interface OidcConfigView {
  issuer: string;
  clientId: string;
  scopes?: string;
  hasClientSecret?: boolean;
}

interface SsoConfigResponse {
  configured: boolean;
  ssoType?: 'saml' | 'oidc';
  enforced?: boolean;
  jitRole?: 'admin' | 'member' | 'viewer';
  config?: SamlConfigView | OidcConfigView;
  spEntityId?: string;
  spAcsUrl?: string;
  callbackUrl?: string;
}

interface DomainRow {
  domain: string;
  verified_at: string | null;
  created_at: string;
}

/** Pull the active org id out of the rynora JWT. JWTs are base64url
 *  segments split by `.` — we only need the payload (segment 1). */
function readActiveOrgFromToken(): { orgId: string | null; userId: string | null } {
  const token = localStorage.getItem('rynora_token');
  if (!token) return { orgId: null, userId: null };
  const parts = token.split('.');
  if (parts.length < 2) return { orgId: null, userId: null };
  try {
    const json = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return {
      orgId: typeof json.activeOrgId === 'string' ? json.activeOrgId : null,
      userId: typeof json.userId === 'string' ? json.userId : null,
    };
  } catch {
    return { orgId: null, userId: null };
  }
}

async function authedFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem('rynora_token');
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export function SsoSettingsPage({ onBack }: SsoSettingsPageProps) {
  const { orgId } = useMemo(readActiveOrgFromToken, []);

  const [activeTab, setActiveTab] = useState<'saml' | 'oidc' | 'domains'>('saml');
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgSummary | null>(null);
  const [config, setConfig] = useState<SsoConfigResponse | null>(null);
  const [domains, setDomains] = useState<DomainRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingForm, setSavingForm] = useState<'saml' | 'oidc' | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  // SAML form state
  const [samlXml, setSamlXml] = useState('');
  const [samlEnforced, setSamlEnforced] = useState(false);
  const [samlJitRole, setSamlJitRole] = useState<'admin' | 'member' | 'viewer'>('member');

  // OIDC form state
  const [oidcIssuer, setOidcIssuer] = useState('');
  const [oidcClientId, setOidcClientId] = useState('');
  const [oidcClientSecret, setOidcClientSecret] = useState('');
  const [oidcScopes, setOidcScopes] = useState('openid email profile');
  const [oidcKeepSecret, setOidcKeepSecret] = useState(true);
  const [oidcEnforced, setOidcEnforced] = useState(false);
  const [oidcJitRole, setOidcJitRole] = useState<'admin' | 'member' | 'viewer'>('member');

  // Domain form state
  const [newDomain, setNewDomain] = useState('');

  // Load org summary list (so the dropdown shows real names) and pick
  // the JWT-active org as the default selection.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch<{ orgs: OrgSummary[] }>('/api/orgs');
        if (cancelled) return;
        setOrgs(res.orgs);
        const initial = res.orgs.find((o) => o.id === orgId) || res.orgs[0] || null;
        setSelectedOrg(initial);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load organizations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Whenever the selected org changes, refetch its SSO config + domains.
  useEffect(() => {
    if (!selectedOrg) return;
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const [cfgRes, domRes] = await Promise.all([
          authedFetch<SsoConfigResponse>(`/api/orgs/${selectedOrg.id}/sso`),
          authedFetch<{ domains: DomainRow[] }>(`/api/orgs/${selectedOrg.id}/sso/domains`),
        ]);
        if (cancelled) return;
        setConfig(cfgRes);
        setDomains(domRes.domains);

        // Hydrate forms from persisted config so the admin sees what's
        // currently active and can edit-in-place.
        if (cfgRes.configured && cfgRes.ssoType === 'saml') {
          const cfg = (cfgRes.config || {}) as SamlConfigView;
          setSamlXml(cfg.idpMetadataXml || '');
          setSamlEnforced(!!cfgRes.enforced);
          setSamlJitRole((cfgRes.jitRole as typeof samlJitRole) || 'member');
          setActiveTab('saml');
        } else if (cfgRes.configured && cfgRes.ssoType === 'oidc') {
          const cfg = (cfgRes.config || {}) as OidcConfigView;
          setOidcIssuer(cfg.issuer || '');
          setOidcClientId(cfg.clientId || '');
          setOidcScopes(cfg.scopes || 'openid email profile');
          setOidcKeepSecret(!!cfg.hasClientSecret);
          setOidcClientSecret('');
          setOidcEnforced(!!cfgRes.enforced);
          setOidcJitRole((cfgRes.jitRole as typeof oidcJitRole) || 'member');
          setActiveTab('oidc');
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load SSO configuration.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedOrg]);

  const flash = (msg: string) => {
    setSavedFlash(msg);
    setTimeout(() => setSavedFlash(null), 3000);
  };

  const handleSaveSaml = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setError(null);
    setSavingForm('saml');
    try {
      const body = {
        idpMetadataXml: samlXml,
        enforced: samlEnforced,
        jitRole: samlJitRole,
      };
      const res = await authedFetch<SsoConfigResponse>(`/api/orgs/${selectedOrg.id}/sso/saml`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setConfig({ ...res, configured: true, ssoType: 'saml' });
      flash('SAML configuration saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save SAML config.');
    } finally {
      setSavingForm(null);
    }
  };

  const handleSaveOidc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setError(null);
    setSavingForm('oidc');
    try {
      const body: Record<string, unknown> = {
        issuer: oidcIssuer,
        clientId: oidcClientId,
        scopes: oidcScopes,
        enforced: oidcEnforced,
        jitRole: oidcJitRole,
      };
      // If the admin pasted a new secret, send it. Otherwise tell the
      // server to keep the existing encrypted value (so editing the
      // issuer or client id alone doesn't wipe the secret).
      if (oidcClientSecret) {
        body.clientSecret = oidcClientSecret;
      } else if (oidcKeepSecret) {
        body.keepExistingSecret = true;
      }
      const res = await authedFetch<SsoConfigResponse>(`/api/orgs/${selectedOrg.id}/sso/oidc`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setConfig({ ...res, configured: true, ssoType: 'oidc' });
      setOidcClientSecret('');
      setOidcKeepSecret(true);
      flash('OIDC configuration saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save OIDC config.');
    } finally {
      setSavingForm(null);
    }
  };

  const handleDisableSso = async () => {
    if (!selectedOrg) return;
    if (!window.confirm('Disable SSO for this organization? Existing SSO users will keep their access.')) {
      return;
    }
    setError(null);
    try {
      await authedFetch(`/api/orgs/${selectedOrg.id}/sso`, { method: 'DELETE' });
      setConfig({ configured: false });
      flash('SSO disabled.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable SSO.');
    }
  };

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg || !newDomain.trim()) return;
    setError(null);
    try {
      await authedFetch(`/api/orgs/${selectedOrg.id}/sso/domains`, {
        method: 'POST',
        body: JSON.stringify({ domain: newDomain.trim().toLowerCase() }),
      });
      const refreshed = await authedFetch<{ domains: DomainRow[] }>(`/api/orgs/${selectedOrg.id}/sso/domains`);
      setDomains(refreshed.domains);
      setNewDomain('');
      flash('Domain added.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add domain.');
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    if (!selectedOrg) return;
    if (!window.confirm(`Stop routing ${domain} through this org's SSO?`)) return;
    setError(null);
    try {
      await authedFetch(`/api/orgs/${selectedOrg.id}/sso/domains/${encodeURIComponent(domain)}`, {
        method: 'DELETE',
      });
      setDomains((prev) => prev.filter((d) => d.domain !== domain));
      flash('Domain removed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove domain.');
    }
  };

  if (loading) {
    return (
      <div className="sso-settings-page">
        <div className="sso-settings-loading">
          <Loader2 size={28} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sso-settings-page">
      <header className="sso-settings-header">
        <button className="sso-settings-back" onClick={onBack} type="button">
          <ArrowLeft size={16} /> Back to dashboard
        </button>
        <BrandMark variant="compact" />
      </header>

      <main className="sso-settings-main">
        <div className="sso-settings-title">
          <Building2 size={22} aria-hidden="true" />
          <div>
            <h1>Single Sign-On</h1>
            <p>Connect your identity provider so your team can sign in with your existing accounts.</p>
          </div>
        </div>

        {orgs.length > 1 && (
          <div className="sso-settings-org-picker">
            <label htmlFor="sso-org-select">Organization</label>
            <select
              id="sso-org-select"
              value={selectedOrg?.id || ''}
              onChange={(e) => setSelectedOrg(orgs.find((o) => o.id === e.target.value) || null)}
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {error && <div className="sso-settings-error">{error}</div>}
        {savedFlash && <div className="sso-settings-flash">{savedFlash}</div>}

        {config?.configured && (
          <div className="sso-settings-status">
            <strong>{config.ssoType === 'saml' ? 'SAML' : 'OIDC'}</strong> SSO is currently active.
            {config.enforced
              ? ' Members must sign in through your IdP.'
              : ' Members can sign in via your IdP or with email/password.'}
            <button className="sso-settings-link-btn" onClick={handleDisableSso} type="button">
              Disable SSO
            </button>
          </div>
        )}

        <div className="sso-settings-tabs">
          <button
            className={`sso-settings-tab ${activeTab === 'saml' ? 'active' : ''}`}
            onClick={() => setActiveTab('saml')}
            type="button"
          >
            SAML 2.0
          </button>
          <button
            className={`sso-settings-tab ${activeTab === 'oidc' ? 'active' : ''}`}
            onClick={() => setActiveTab('oidc')}
            type="button"
          >
            OIDC <span className="sso-settings-pill">Microsoft Entra · Okta · Auth0</span>
          </button>
          <button
            className={`sso-settings-tab ${activeTab === 'domains' ? 'active' : ''}`}
            onClick={() => setActiveTab('domains')}
            type="button"
          >
            Email domains
          </button>
        </div>

        {activeTab === 'saml' && (
          <form className="sso-settings-form" onSubmit={handleSaveSaml}>
            <div className="sso-settings-field">
              <label htmlFor="sso-saml-xml">IdP metadata XML</label>
              <p className="sso-settings-hint">
                Paste the entire XML document your identity provider gives you (Okta calls this the "Identity Provider
                metadata"; Microsoft Entra calls it "App Federation Metadata").
              </p>
              <textarea
                id="sso-saml-xml"
                rows={10}
                value={samlXml}
                onChange={(e) => setSamlXml(e.target.value)}
                placeholder="<?xml version='1.0'?>&#10;<EntityDescriptor entityID='https://your-idp.example.com/...'>&#10;  ..."
                spellCheck={false}
                required
              />
            </div>

            {selectedOrg && (
              <div className="sso-settings-callout">
                <strong>Service Provider details</strong>
                <p>Hand these to your IdP admin to register Rynora as a SAML SP.</p>
                <ul>
                  <li>
                    <span>Entity ID:</span>{' '}
                    <code>{config?.spEntityId || `${API_BASE}/sso/${selectedOrg.slug}/saml/metadata`}</code>
                  </li>
                  <li>
                    <span>ACS URL:</span>{' '}
                    <code>{config?.spAcsUrl || `${API_BASE}/api/sso/${selectedOrg.slug}/saml/acs`}</code>
                  </li>
                  <li>
                    <span>Metadata URL:</span>{' '}
                    <a href={`${API_BASE}/api/sso/${selectedOrg.slug}/saml/metadata`} target="_blank" rel="noreferrer">
                      Download SP metadata <ExternalLink size={12} />
                    </a>
                  </li>
                </ul>
              </div>
            )}

            <SsoEnforcementBlock
              enforced={samlEnforced}
              onEnforcedChange={setSamlEnforced}
              jitRole={samlJitRole}
              onJitRoleChange={setSamlJitRole}
            />

            <button className="sso-settings-submit" type="submit" disabled={savingForm === 'saml'}>
              {savingForm === 'saml' ? 'Saving…' : 'Save SAML configuration'}
            </button>
          </form>
        )}

        {activeTab === 'oidc' && (
          <form className="sso-settings-form" onSubmit={handleSaveOidc}>
            <div className="sso-settings-field">
              <label htmlFor="sso-oidc-issuer">Issuer URL</label>
              <p className="sso-settings-hint">
                The base URL of your OpenID Connect provider — Rynora discovers the rest from{' '}
                <code>/.well-known/openid-configuration</code>. For Microsoft Entra ID this looks like{' '}
                <code>https://login.microsoftonline.com/&lt;tenant&gt;/v2.0</code>.
              </p>
              <input
                id="sso-oidc-issuer"
                type="url"
                value={oidcIssuer}
                onChange={(e) => setOidcIssuer(e.target.value)}
                placeholder="https://login.microsoftonline.com/your-tenant-id/v2.0"
                required
              />
            </div>

            <div className="sso-settings-grid-2">
              <div className="sso-settings-field">
                <label htmlFor="sso-oidc-client-id">Client ID</label>
                <input
                  id="sso-oidc-client-id"
                  type="text"
                  value={oidcClientId}
                  onChange={(e) => setOidcClientId(e.target.value)}
                  required
                />
              </div>
              <div className="sso-settings-field">
                <label htmlFor="sso-oidc-client-secret">Client secret {oidcKeepSecret ? '(stored)' : ''}</label>
                <input
                  id="sso-oidc-client-secret"
                  type="password"
                  value={oidcClientSecret}
                  onChange={(e) => setOidcClientSecret(e.target.value)}
                  placeholder={oidcKeepSecret ? '•••••••••• (leave blank to keep)' : 'paste secret'}
                />
              </div>
            </div>

            <div className="sso-settings-field">
              <label htmlFor="sso-oidc-scopes">Scopes</label>
              <input
                id="sso-oidc-scopes"
                type="text"
                value={oidcScopes}
                onChange={(e) => setOidcScopes(e.target.value)}
                placeholder="openid email profile"
              />
            </div>

            {selectedOrg && (
              <div className="sso-settings-callout">
                <strong>Redirect URI</strong>
                <p>Register this URL with your IdP as a valid redirect / callback URI.</p>
                <code>{config?.callbackUrl || `${API_BASE}/api/sso/${selectedOrg.slug}/oidc/callback`}</code>
              </div>
            )}

            <SsoEnforcementBlock
              enforced={oidcEnforced}
              onEnforcedChange={setOidcEnforced}
              jitRole={oidcJitRole}
              onJitRoleChange={setOidcJitRole}
            />

            <button className="sso-settings-submit" type="submit" disabled={savingForm === 'oidc'}>
              {savingForm === 'oidc' ? 'Saving…' : 'Save OIDC configuration'}
            </button>
          </form>
        )}

        {activeTab === 'domains' && (
          <div className="sso-settings-domains">
            <p className="sso-settings-hint">
              Map an email domain to this organization so anyone signing in with <code>name@yourdomain.com</code> is
              automatically routed through your IdP. Domains are auto-verified for now — DNS TXT verification is on the
              roadmap.
            </p>

            <form className="sso-settings-domain-form" onSubmit={handleAddDomain}>
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="acme.com"
                pattern="^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
                required
              />
              <button type="submit" className="sso-settings-submit sso-settings-submit--inline">
                <Plus size={14} /> Add
              </button>
            </form>

            <ul className="sso-settings-domain-list">
              {domains.length === 0 && <li className="sso-settings-domain-empty">No domains claimed yet.</li>}
              {domains.map((d) => (
                <li key={d.domain}>
                  <span>{d.domain}</span>
                  {d.verified_at ? (
                    <span className="sso-settings-pill sso-settings-pill--ok">verified</span>
                  ) : (
                    <span className="sso-settings-pill">pending</span>
                  )}
                  <button type="button" onClick={() => handleRemoveDomain(d.domain)}>
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}

interface EnforcementBlockProps {
  enforced: boolean;
  onEnforcedChange: (next: boolean) => void;
  jitRole: 'admin' | 'member' | 'viewer';
  onJitRoleChange: (next: 'admin' | 'member' | 'viewer') => void;
}

function SsoEnforcementBlock({ enforced, onEnforcedChange, jitRole, onJitRoleChange }: EnforcementBlockProps) {
  return (
    <div className="sso-settings-grid-2">
      <label className="sso-settings-checkbox">
        <input type="checkbox" checked={enforced} onChange={(e) => onEnforcedChange(e.target.checked)} />
        <span>
          <strong>Enforce SSO</strong>
          <small>Require all members to sign in through your IdP (no email/password).</small>
        </span>
      </label>
      <div className="sso-settings-field">
        <label htmlFor="sso-jit-role">Default role for new SSO users</label>
        <select id="sso-jit-role" value={jitRole} onChange={(e) => onJitRoleChange(e.target.value as typeof jitRole)}>
          <option value="viewer">Viewer (read-only)</option>
          <option value="member">Member (read + write)</option>
          <option value="admin">Admin (full access except billing)</option>
        </select>
      </div>
    </div>
  );
}
